import { css } from 'aphrodite';
import moment from 'moment-timezone';
import * as React from 'react';
import defaultEventImage from '../../../web/assets/default_event_image.svg';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill/Pill';
import { IEventLandingItem } from '../../queries/useEventsLanding';
import { isInIframe } from '../../utils/iframe';
import { styles } from './styles';

interface IEventCardProps {
	event: IEventLandingItem;
	compact?: boolean;
}

export const EventCard: React.FC<IEventCardProps> = ({ event, compact = false }) => {
	/** Formats date for display. Uses 'en-US' locale for consistency on public pages. */
	const formatDateTime = (dateStr: string, timeZone?: string) => {
		const date = new Date(dateStr);
		if (isNaN(date.getTime())) return 'Date not available';
		const options: Intl.DateTimeFormatOptions = {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			timeZone,
		};
		return date.toLocaleString('en-US', options);
	};

	/**
	 * Handles registration button clicks with context-aware navigation.
	 * When embedded in an iframe (e.g., website widget), opens in new tab to avoid
	 * navigation issues and maintain user's browsing context. When standalone,
	 * navigates in same window for simpler UX.
	 */
	const handleRegisterClick = (e: React.MouseEvent) => {
		e.preventDefault();
		const url = event.anonymousLink;
		if (!url || !/^https?:\/\//i.test(url)) return;

		if (isInIframe()) {
			window.open(url, '_blank');
		} else {
			window.location.href = url;
		}
	};

	/**
	 * Determines if this is a past event by checking if the event's end time
	 * (or start time if no end time) has passed.
	 */
	const isPastEvent = React.useMemo(() => {
		const eventDate = event.endTime || event.startTime;
		if (!eventDate) return false;
		return new Date(eventDate) < new Date();
	}, [event.endTime, event.startTime]);

	const isDisabled = event.registrationClosed || event.isSoldOut || isPastEvent;
	const buttonText = event.isSoldOut
		? 'Sold Out'
		: event.registrationClosed
			? 'Registration Closed'
			: isPastEvent
				? 'Event Ended'
				: 'Register';

	const eventImage = event.imageUrl || defaultEventImage;

	return (
		<div className={css(compact ? styles.cardCompact : styles.card)}>
			{!compact && (
				<figure className={css(styles.eventImageContainer)}>
					<img className={css(styles.eventImage)} src={eventImage} alt={`Event graphic for ${event.name}`} />
				</figure>
			)}
			<div className={css(compact ? styles.cardContentCompact : styles.cardContent)}>
				<div className={css(styles.eventNameRow)}>
					<h3 className={css(compact ? styles.eventNameCompact : styles.eventName)}>{event.name}</h3>
					{event.isSoldOut && !compact && <Pill variant='label' color='red' label='Sold Out' size='small' />}
				</div>
				<p className={css(compact ? styles.dateTimeCompact : styles.dateTime)}>
					{formatDateTime(event.startTime, event.timeZone)}
					{!compact && event.timeZone && ` (${moment.tz(event.startTime, event.timeZone).format('z')})`}
				</p>
				{!compact && event.location && <p className={css(styles.location)}>{event.location}</p>}
				{!compact && event.details && <p className={css(styles.details)}>{event.details}</p>}
			</div>
			<Button
				onClick={handleRegisterClick}
				disabled={isDisabled}
				variant={isDisabled ? 'secondary' : 'primary'}
				size={compact ? 'sm' : 'lg'}
			>
				{buttonText}
			</Button>
		</div>
	);
};
